<script setup lang="ts">
const { t } = useI18n()

useHead({
  titleTemplate: () => t("viber.title") || t("seo.title"),
  meta: [
    { name: "description", content: () => t("viber.subtitle") || t("seo.description") },
    { property: "og:title", content: () => t("viber.title") || t("seo.title") },
    { property: "og:description", content: () => t("viber.subtitle") || t("seo.description") },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary_large_image" }
  ]
})
</script>

<template>
  <div>
    <div class="main-banner">
      <div class="main-banner-content">
        <div class="banner-text">
          <div class="banner-title">
            {{ $t('viber.title') }}
          </div>
          <div class="banner-description">
            {{ $t('viber.subtitle') }}
          </div>
          <div class="banner-action">
            <NuxtLink class="nav-link button filled whitespace-nowrap !text-sm">
              <p>{{ $t('actions.tryForFree') }}</p>
            </NuxtLink>
            <NuxtLink to="viber://chat?service=26076" class="button outlined" external>{{ $t('actions.contactUs') }}
            </NuxtLink>
          </div>
        </div>
        <div class="w-full flex items-center">
          <div class="bg-gray-100 w-full h-4/5 min-w-[50%] min-h-[300px] mt-4"></div>
        </div>
      </div>
    </div>

    <div class="numbers flex flex-col gap-[4rem] w-full xl:w-[1200px] p-5 mx-auto">
      <div class="flex flex-col gap-[4rem] w-full md:w-2/3  2xl:w-1/2">
        <p class="text-[#3070b6] font-bols text-3xl lg:text-4xl">{{ $t('viber.numbers.title') }}</p>
        <p class="text-xl">{{ $t('viber.numbers.subtitle') }}</p>
      </div>
      <div class="flex flex-col md:flex-row gap-8">
        <div class="flex flex-col gap-2 justify-start grow">
          <p class="text-[#3070b6] font-bold text-5xl border-b-2 pb-4">{{ $t('viber.numbers.elems[0].number') }}</p>
          <p>{{ $t('viber.numbers.elems[0].text') }}</p>
        </div>
        <div class="flex flex-col gap-2 justify-start grow">
          <p class="text-[#3070b6] font-bold text-5xl border-b-2 pb-4">{{ $t('viber.numbers.elems[1].number') }}</p>
          <p>{{ $t('viber.numbers.elems[1].text') }}</p>
        </div>
        <div class="flex flex-col gap-2 justify-start grow">
          <p class="text-[#3070b6] font-bold text-5xl border-b-2 pb-4">{{ $t('viber.numbers.elems[2].number') }}</p>
          <p>{{ $t('viber.numbers.elems[2].text') }}</p>
        </div>
      </div>
    </div>

    <div class="page-section service-advantages !pt-20">
      <div class="container">
        <div class="section-content">
          <div class="advantages-title">
            {{ $t('viber.results.title') }}
          </div>
          {{ $t('viber.results.subtitle') }}
          <div class="advantages-list mt-8">
            <div class="advantage-item">
              <div class="item-count">01</div>
              <div class="item-title break-all md:break-normal">{{ $t('viber.results.elems[0].title') }}:</div>
              <div class="item-text break-all md:break-normal">
                {{ $t('viber.results.elems[0].text') }}
              </div>
            </div>
            <div class="advantage-item">
              <div class="item-count">02</div>
              <div class="item-title break-all md:break-normal">{{ $t('viber.results.elems[1].title') }}:</div>
              <div class="item-text break-all md:break-normal">
                {{ $t('viber.results.elems[1].text') }}
              </div>
            </div>
            <div class="advantage-item">
              <div class="item-count">03</div>
              <div class="item-title break-all md:break-normal">{{ $t('viber.results.elems[2].title') }}:</div>
              <div class="item-text break-all md:break-normal">
                {{ $t('viber.results.elems[2].text') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="page-section">
      <div class="container">
        <div class="section-head text-center">
          <div class="section-head-title">{{ $t('viber.journey.title') }}</div>
        </div>
        <div class="section-content">
          <div class="about-section flex flex-row gap-16">
            <div class="service-hint flex flex-[50%] flex-col">
              <div class="h-56 bg-gray-100 mb-2"></div>
              <span class="highlight-blue mb-2 font-bold text-xl">{{ $t('viber.journey.elems[0].title') }}</span>
              {{ $t('viber.journey.elems[0].text') }}
            </div>
            <div class="service-hint flex flex-[50%] flex-col">
              <div class="h-56 bg-gray-100 mb-2"></div>
              <span class="highlight-blue mb-2 font-bold text-xl">
                {{ $t('viber.journey.elems[1].title') }}
              </span>
              {{ $t('viber.journey.elems[1].text') }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="page-section">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title after:opacity-0">{{ $t('viber.points.title') }}</div>
        </div>
        <div class="section-content">
          <div class="about-section flex flex-col gap-16">
            <div class="about-item">
              <div class="about-item-text mx-auto mx-auto flex flex-col">
                <span class="highlight-blue mb-2 font-bold text-xl">{{ $t('viber.points.promo.title') }}</span>
                {{ $t('viber.points.promo.subtitle') }}
                <ul class="my-4">
                  <p>&#x2022; {{ $t('viber.points.promo.elems[0]') }}</p>
                  <p>&#x2022; {{ $t('viber.points.promo.elems[1]') }}</p>
                  <p>&#x2022; {{ $t('viber.points.promo.elems[2]') }}</p>
                  <p>&#x2022; {{ $t('viber.points.promo.elems[3]') }}</p>
                  <p>&#x2022; {{ $t('viber.points.promo.elems[4]') }}</p>
                </ul>
              </div>
              <div class="bg-gray-100 w-full min-h-[400px]"></div>
            </div>
            <div class="about-item">
              <div class="bg-gray-100 w-full min-h-[300px]"></div>
              <div class="about-item-text mx-auto mx-auto flex flex-col">
                <span class="highlight-blue mb-2 font-bold text-xl">{{ $t('viber.points.trans.title') }}</span>
                {{ $t('viber.points.trans.subtitle') }}
                <ul class="my-4">
                  <p>&#x2022; {{ $t('viber.points.trans.elems[0]') }}</p>
                  <p>&#x2022; {{ $t('viber.points.trans.elems[1]') }}</p>
                  <p>&#x2022; {{ $t('viber.points.trans.elems[2]') }}</p>
                </ul>
              </div>
            </div>
            <div class="about-item">
              <div class="about-item-text mx-auto mx-auto flex flex-col">
                <span class="highlight-blue mb-2 font-bold text-xl">{{ $t('viber.points.dialog.title') }}</span>
                {{ $t('viber.points.dialog.subtitle') }}
                <ul class="my-4">
                  <p>&#x2022; {{ $t('viber.points.dialog.elems[0]') }}</p>
                  <p>&#x2022; {{ $t('viber.points.dialog.elems[1]') }}</p>
                  <p>&#x2022; {{ $t('viber.points.dialog.elems[2]') }}</p>
                  <p>&#x2022; {{ $t('viber.points.dialog.elems[3]') }}</p>
                </ul>
              </div>
              <div class="bg-gray-100 w-full min-h-[400px]"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="page-section">
      <div class="container">
        <div class="section-head text-center">
          <div class="section-head-title after:opacity-0">{{ $t('viber.way.title') }}</div>
        </div>
        <div class="section-content">
          <div class="about-section flex flex-row gap-16">
            <div class="service-hint flex flex-[50%] flex-col">
              <span class="highlight-blue mb-2 font-bold text-xl">{{ $t('viber.way.elems[0].title') }}</span>
              {{ $t('viber.way.elems[0].text') }}
            </div>
            <div class="service-hint flex flex-[50%] flex-col">
              <span class="highlight-blue mb-2 font-bold text-xl">
                {{ $t('viber.way.elems[1].title') }}
              </span>
              {{ $t('viber.way.elems[1].text') }}
            </div>
            <div class="service-hint flex flex-[50%] flex-col">
              <span class="highlight-blue mb-2 font-bold text-xl">
                {{ $t('viber.way.elems[2].title') }}
              </span>
              {{ $t('viber.way.elems[2].text') }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="api my-12 flex flex-col justify-center md:flex-row gap-[4rem] w-full xl:w-5/6 2xl:w-4/6 p-5 mx-auto">
      <div class="img bg-gray-100 w-full lg:w-[50%] h-[400px]"></div>
      <div class="text flex flex-col w-full lg:w-[50%]">
        <p class="text-[#3070b6] font-bold text-4xl">{{ $t('viber.api.title') }}</p>
        <p class="flex-auto my-4 font-bold">{{ $t('viber.api.subtitle') }}</p>
        <ul class="mb-8">
          <li>&#x2022; {{ $t('viber.api.elems[0]') }}</li>
          <li>&#x2022; {{ $t('viber.api.elems[1]') }}</li>
          <li>&#x2022; {{ $t('viber.api.elems[2]') }}</li>
          <li>&#x2022; {{ $t('viber.api.elems[3]') }}</li>
        </ul>
        <div class="banner-action">
          <NuxtLink class="nav-link button filled whitespace-nowrap !text-sm">
            <p>{{ $t('viber.api.action') }}</p>
          </NuxtLink>
        </div>
      </div>
    </div>

    <div
      class="api my-12 flex flex-col-reverse column-reverse justify-center md:flex-row gap-[4rem] w-full xl:w-5/6 2xl:w-4/6 p-5 mx-auto">
      <div class="text flex flex-col w-full lg:w-[50%]">
        <p class="text-[#3070b6] font-bold text-4xl">{{ $t('viber.apps.title') }}</p>
        <p class="flex-auto my-4 font-bold">{{ $t('viber.apps.subtitle') }}</p>
        <div class="banner-action">
          <NuxtLink class="nav-link button filled !text-sm">
            <p>{{ $t('viber.apps.action') }}</p>
          </NuxtLink>
        </div>
      </div>
      <div class="img bg-gray-100 w-full lg:w-[50%] h-[300px]"></div>
    </div>

    <div class="page-section callback-section">
      <div class="callback-section-img">
        <NuxtImg src="/images/callback.png" width="80" height="80" alt="Callback Icon" format="webp" loading="lazy" />
      </div>
      <div class="pr-8 text-right w-full pl-36 md:pl-0 md:w-[50%] flex flex-col justify-right items-end pr-0 xl:pr-36">
        <div class="section-head">
          <div class="section-head-title !text-right after:opacity-0">{{ $t('viber.contact.title') }}</div>
        </div>
        <p class="mb-16 w-4/5">{{ $t('viber.contact.subtitle') }}</p>
        <div class="banner-action">
          <a class="button outlined">{{ $t('viber.contact.action') }}</a>
        </div>
      </div>
    </div>

    <div class="flex flex-col items-center justify-center text-center">
      <div class="pt-8 lg:pt-0 pb-16 px-8 flex flex-col justify-center items-center gap-8">
        <p class="text-[#3070b6] leading-[120%] font-bold text-3xl md:text-5xl w-full sm:w-2/3 lg:w-1/2">{{
          $t('viber.footerBlock.title') }}</p>
        <p class="text-lg font-bold">{{ $t('viber.footerBlock.subtitle') }}</p>
        <div class="flex flex-col md:flex-row items-center gap-8">
          <NuxtLink class="nav-link button filled whitespace-nowrap !text-sm">
            <p>{{ $t('actions.tryForFree') }}</p>
          </NuxtLink>
          <a class="button outlined">{{ $t('actions.contactUs') }}</a>
        </div>
      </div>
    </div>

  </div>
</template>

<style lang="scss" scoped>
.main-banner .banner-text {
  margin-left: 0;
  align-self: center;
  max-width: 700px;
}

.main-banner-content {
  padding: 0;
}

@media (min-width: 768px) {
  .main-banner .banner-text {
    max-width: 500px;
  }
}

@media (min-width: 1170px) {
  .main-banner .banner-text {
    max-width: 500px;
  }
}

@media (min-width: 1300px) {
  .main-banner .banner-text {
    margin-left: calc((100% - 1300px) / 2);
    max-width: 700px;
  }
}
</style>